import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../context/LanguageContext';
import './BrokerApplicationModal.css';

const IcCheck = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>);
const IcClose = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>);

const EMPTY_FORM = {
  fullName: '', email: '', phone: '',
  agencyName: '', officeAddress: '', website: '',
  yearsInBusiness: '',
  creaaLicense: '', heNumber: '', vatNumber: '',
  districts: [], propertyTypes: [], languages: [],
  bio: '', workingHours: '',
  termsAccepted: false,
};

const EMPTY_DOCS = {
  creaaDoc: null, govId: null, addressProof: null,
  profilePhoto: null,
};

export default function BrokerApplicationModal({ open, onClose }) {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [docFiles, setDocFiles] = useState(EMPTY_DOCS);
  const [langInput, setLangInput] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const update = (f, v) => { setForm(p => ({ ...p, [f]: v })); setErrors(p => ({ ...p, [f]: null })); };

  const toggleMulti = (field, value) => {
    setForm(p => ({
      ...p,
      [field]: p[field].includes(value)
        ? p[field].filter(x => x !== value)
        : [...p[field], value],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim())       e.fullName = t('findBroker.errors.required');
    if (!form.email.includes('@'))   e.email = t('findBroker.errors.validEmail');
    if (!form.phone.trim())          e.phone = t('findBroker.errors.required');
    if (!form.agencyName.trim())     e.agencyName = t('findBroker.errors.required');
    if (!form.officeAddress.trim())  e.officeAddress = t('findBroker.errors.required');
    if (!form.yearsInBusiness)       e.yearsInBusiness = t('findBroker.errors.required');
    if (!form.creaaLicense.trim())   e.creaaLicense = t('findBroker.errors.required');
    if (!form.heNumber.trim())       e.heNumber = t('findBroker.errors.required');
    if (!form.bio.trim() || form.bio.trim().length < 50) e.bio = 'Bio must be at least 50 characters';
    if (!form.termsAccepted)         e.termsAccepted = 'Please accept the terms';
    if (!docFiles.creaaDoc)          e.creaaDoc = 'Required';
    if (!docFiles.govId)             e.govId = 'Required';
    if (!docFiles.addressProof)      e.addressProof = 'Required';
    if (!docFiles.profilePhoto)      e.profilePhoto = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const uploadDoc = async (file, path) => {
    if (!file) return null;
    const ext = file.name.split('.').pop();
    const fileName = `${form.email}/${path}.${ext}`;
    const { error } = await supabase.storage
      .from('agent-verification-docs')
      .upload(fileName, file, { upsert: true });
    if (error) return null;
    const { data } = supabase.storage.from('agent-verification-docs').getPublicUrl(fileName);
    return data?.publicUrl || null;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setUploading(true);
    try {
      const [creaaDocUrl, govIdUrl, addressProofUrl, profilePhotoUrl] = await Promise.all([
        uploadDoc(docFiles.creaaDoc, 'creaa_license'),
        uploadDoc(docFiles.govId, 'gov_id'),
        uploadDoc(docFiles.addressProof, 'address_proof'),
        uploadDoc(docFiles.profilePhoto, 'profile_photo'),
      ]);
      const { error } = await supabase.from('broker_applications').insert({
        name: form.fullName,
        email: form.email,
        phone: form.phone,
        agency: form.agencyName,
        office_address: form.officeAddress,
        website: form.website || null,
        years_in_business: form.yearsInBusiness,
        etek: form.creaaLicense,
        he_number: form.heNumber,
        vat_number: form.vatNumber || null,
        districts: form.districts,
        property_types: form.propertyTypes,
        languages: form.languages,
        bio: form.bio,
        working_hours: form.workingHours || null,
        creaa_license_doc_url: creaaDocUrl,
        government_id_url: govIdUrl,
        address_proof_url: addressProofUrl,
        profile_photo_url: profilePhotoUrl,
      });
      if (error) { console.error('broker_applications insert failed:', error); }
    } catch (err) {
      console.error('Submit error:', err);
    }
    setUploading(false);
    setStep(2);
  };

  const closeModal = () => {
    onClose();
    setTimeout(() => {
      setStep(1);
      setForm(EMPTY_FORM);
      setDocFiles(EMPTY_DOCS);
      setLangInput('');
      setErrors({});
    }, 300);
  };

  if (!open) return null;

  return (
    <div className="fbp-modal-overlay" onClick={closeModal}>
      <div className="fbp-modal" onClick={e => e.stopPropagation()}>

        {step === 1 ? (
          <>
            <div className="fbp-modal__header">
              <div>
                <div className="fbp-modal__eyebrow">{t('findBroker.modal.eyebrow')}</div>
                <h2 className="fbp-modal__title">{t('findBroker.modal.title')}</h2>
                <p className="fbp-modal__sub">{t('findBroker.modal.sub')}</p>
              </div>
              <button className="fbp-modal__close" onClick={closeModal} aria-label="Close"><IcClose /></button>
            </div>

            <div className="fbp-modal__body">
              {/* Section 1: Personal */}
              <fieldset className="fbp-section">
                <legend className="fbp-section__legend">Personal Information</legend>
                <div className="fbp-modal__row">
                  <div className="fbp-modal__field">
                    <label>Full Legal Name <span className="fbp-req">*</span></label>
                    <input type="text" placeholder="Andreas Georgiou" value={form.fullName} onChange={e => update('fullName', e.target.value)} className={errors.fullName ? 'fbp-err-input' : ''} />
                    {errors.fullName && <span className="fbp-field-err">{errors.fullName}</span>}
                  </div>
                  <div className="fbp-modal__field">
                    <label>Email <span className="fbp-req">*</span></label>
                    <input type="email" placeholder="you@agency.com" value={form.email} onChange={e => update('email', e.target.value)} className={errors.email ? 'fbp-err-input' : ''} />
                    {errors.email && <span className="fbp-field-err">{errors.email}</span>}
                  </div>
                </div>
                <div className="fbp-modal__field fbp-modal__field--full">
                  <label>Phone (+357) <span className="fbp-req">*</span></label>
                  <input type="tel" placeholder="+357 99 000 000" value={form.phone} onChange={e => update('phone', e.target.value)} className={errors.phone ? 'fbp-err-input' : ''} />
                  {errors.phone && <span className="fbp-field-err">{errors.phone}</span>}
                </div>
              </fieldset>

              {/* Section 2: Agency */}
              <fieldset className="fbp-section">
                <legend className="fbp-section__legend">Agency Information</legend>
                <div className="fbp-modal__field fbp-modal__field--full">
                  <label>Agency Name <span className="fbp-req">*</span></label>
                  <input type="text" placeholder="Limassol Prestige Realty" value={form.agencyName} onChange={e => update('agencyName', e.target.value)} className={errors.agencyName ? 'fbp-err-input' : ''} />
                  {errors.agencyName && <span className="fbp-field-err">{errors.agencyName}</span>}
                </div>
                <div className="fbp-modal__field fbp-modal__field--full">
                  <label>Office Address <span className="fbp-req">*</span></label>
                  <input type="text" placeholder="Street, City, Postcode" value={form.officeAddress} onChange={e => update('officeAddress', e.target.value)} className={errors.officeAddress ? 'fbp-err-input' : ''} />
                  {errors.officeAddress && <span className="fbp-field-err">{errors.officeAddress}</span>}
                </div>
                <div className="fbp-modal__row">
                  <div className="fbp-modal__field">
                    <label>Website <span className="fbp-hint">(optional)</span></label>
                    <input type="url" placeholder="https://yoursite.com" value={form.website} onChange={e => update('website', e.target.value)} />
                  </div>
                  <div className="fbp-modal__field">
                    <label>Years in Business <span className="fbp-req">*</span></label>
                    <select value={form.yearsInBusiness} onChange={e => update('yearsInBusiness', e.target.value)} className={errors.yearsInBusiness ? 'fbp-err-input' : ''}>
                      <option value="">Select...</option>
                      {['<1','1-3','3-5','5-10','10+'].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    {errors.yearsInBusiness && <span className="fbp-field-err">{errors.yearsInBusiness}</span>}
                  </div>
                </div>
              </fieldset>

              {/* Section 3: Credentials */}
              <fieldset className="fbp-section">
                <legend className="fbp-section__legend">Credentials & Licensing</legend>
                <p className="fbp-section__note">Cyprus law requires real estate agents to be registered with CREAA (Cyprus Real Estate Agents Association). We verify every license before approval.</p>
                <div className="fbp-modal__row">
                  <div className="fbp-modal__field">
                    <label>CREAA License Number <span className="fbp-req">*</span></label>
                    <input type="text" placeholder="e.g. 1047-229/E" value={form.creaaLicense} onChange={e => update('creaaLicense', e.target.value)} className={errors.creaaLicense ? 'fbp-err-input' : ''} />
                    {errors.creaaLicense && <span className="fbp-field-err">{errors.creaaLicense}</span>}
                  </div>
                  <div className="fbp-modal__field">
                    <label>Business Registration HE Number <span className="fbp-req">*</span></label>
                    <input type="text" placeholder="e.g. HE123456" value={form.heNumber} onChange={e => update('heNumber', e.target.value)} className={errors.heNumber ? 'fbp-err-input' : ''} />
                    {errors.heNumber && <span className="fbp-field-err">{errors.heNumber}</span>}
                  </div>
                </div>
                <div className="fbp-modal__field" style={{maxWidth:240}}>
                  <label>VAT Number <span className="fbp-hint">(optional)</span></label>
                  <input type="text" placeholder="CY12345678X" value={form.vatNumber} onChange={e => update('vatNumber', e.target.value)} />
                </div>
              </fieldset>

              {/* Section 4: Operating Area */}
              <fieldset className="fbp-section">
                <legend className="fbp-section__legend">Operating Area</legend>
                <div className="fbp-modal__field fbp-modal__field--full">
                  <label>Districts you cover</label>
                  <div className="fbp-checks">
                    {['Limassol','Paphos','Nicosia','Larnaca','Famagusta'].map(d => (
                      <label key={d} className="fbp-check-item">
                        <input type="checkbox" checked={form.districts.includes(d)} onChange={() => toggleMulti('districts', d)} /> {d}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="fbp-modal__field fbp-modal__field--full">
                  <label>Property types</label>
                  <div className="fbp-checks">
                    {['Residential','Commercial','Land','Luxury','Investment'].map(ptype => (
                      <label key={ptype} className="fbp-check-item">
                        <input type="checkbox" checked={form.propertyTypes.includes(ptype)} onChange={() => toggleMulti('propertyTypes', ptype)} /> {ptype}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="fbp-modal__field fbp-modal__field--full">
                  <label>Languages spoken</label>
                  <div className="fbp-lang-input-row">
                    <input
                      type="text"
                      placeholder="Type a language and press Enter"
                      value={langInput}
                      onChange={e => setLangInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const lang = langInput.trim();
                          if (lang && !form.languages.includes(lang)) {
                            setForm(p => ({ ...p, languages: [...p.languages, lang] }));
                          }
                          setLangInput('');
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="fbp-lang-add-btn"
                      onClick={() => {
                        const lang = langInput.trim();
                        if (lang && !form.languages.includes(lang)) {
                          setForm(p => ({ ...p, languages: [...p.languages, lang] }));
                        }
                        setLangInput('');
                      }}
                    >+ Add</button>
                  </div>
                  {form.languages.length > 0 && (
                    <div className="fbp-chips">
                      {form.languages.map(lang => (
                        <span key={lang} className="fbp-chip">
                          {lang}
                          <button
                            type="button"
                            className="fbp-chip__remove"
                            onClick={() => setForm(p => ({ ...p, languages: p.languages.filter(l => l !== lang) }))}
                            aria-label={`Remove ${lang}`}
                          >×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </fieldset>

              {/* Section 5: Documents */}
              <fieldset className="fbp-section">
                <legend className="fbp-section__legend">Verification Documents</legend>
                <p className="fbp-section__note">All documents are securely stored and used only for verification. We do not share these with third parties.</p>
                {[
                  { key: 'creaaDoc', label: 'CREAA License Document', hint: 'Your CREAA certificate', accept: '.pdf,image/*', required: true },
                  { key: 'govId', label: 'Government ID', hint: 'Cyprus ID card or passport', accept: '.pdf,image/*', required: true },
                  { key: 'addressProof', label: 'Office Address Proof', hint: 'Recent utility bill or lease agreement', accept: '.pdf,image/*', required: true },
                  { key: 'profilePhoto', label: 'Profile Headshot', hint: 'Professional photo for your public profile', accept: 'image/*', required: true },
                ].map(doc => (
                  <div key={doc.key} className="fbp-modal__field fbp-modal__field--full fbp-doc-field">
                    <label>
                      {doc.label} {doc.required && <span className="fbp-req">*</span>}
                      <span className="fbp-hint"> — {doc.hint}</span>
                    </label>
                    <input
                      type="file"
                      accept={doc.accept}
                      onChange={e => setDocFiles(p => ({ ...p, [doc.key]: e.target.files[0] || null }))}
                    />
                    {docFiles[doc.key] && <span className="fbp-file-chosen">✓ {docFiles[doc.key].name}</span>}
                    {errors[doc.key] && <span className="fbp-field-err">{errors[doc.key]}</span>}
                  </div>
                ))}
              </fieldset>

              {/* Section 6: Public Profile */}
              <fieldset className="fbp-section">
                <legend className="fbp-section__legend">Public Profile</legend>
                <div className="fbp-modal__field fbp-modal__field--full">
                  <label>Short Bio <span className="fbp-req">*</span> <span className="fbp-hint">(150–300 chars)</span></label>
                  <textarea
                    placeholder="Tell clients about your experience, track record, and what makes you stand out..."
                    value={form.bio}
                    onChange={e => update('bio', e.target.value)}
                    rows={4}
                    maxLength={300}
                    className={errors.bio ? 'fbp-err-input' : ''}
                  />
                  <span className="fbp-char-count">{form.bio.length}/300</span>
                  {errors.bio && <span className="fbp-field-err">{errors.bio}</span>}
                </div>
                <div className="fbp-modal__field fbp-modal__field--full">
                  <label>Working Hours <span className="fbp-hint">(optional)</span></label>
                  <input type="text" placeholder="e.g. Mon-Fri 9:00-18:00" value={form.workingHours} onChange={e => update('workingHours', e.target.value)} />
                </div>
              </fieldset>

              {/* Terms */}
              <div className="fbp-modal__field fbp-modal__field--full">
                <label className="fbp-checkbox-label">
                  <input type="checkbox" checked={form.termsAccepted} onChange={e => update('termsAccepted', e.target.checked)} />
                  I confirm all information provided is accurate. I understand CosmoEstates will verify my credentials.
                </label>
                {errors.termsAccepted && <span className="fbp-field-err">{errors.termsAccepted}</span>}
              </div>
            </div>

            <div className="fbp-modal__footer">
              <button className="fbp-modal__cancel" onClick={closeModal}>Cancel</button>
              <button className="fbp-modal__submit" onClick={handleSubmit} disabled={uploading}>
                {uploading ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
            <p className="fbp-modal__footer-note">We review applications within 24-48 hours. You'll receive an email once your application has been processed.</p>
          </>
        ) : (
          <div className="fbp-modal__success">
            <div className="fbp-modal__success-icon"><IcCheck /></div>
            <h2>{t('findBroker.modal.successTitle')}</h2>
            <p>{t('findBroker.modal.successSub')}</p>
            <button className="fbp-modal__submit" onClick={closeModal} style={{maxWidth:200}}>{t('findBroker.modal.done')}</button>
          </div>
        )}
      </div>
    </div>
  );
}
